import 'dayjs/locale/fr-ca'

import type React from 'react'
import { Suspense, useMemo } from 'react'

import { LOUNGE_CHANNEL_ID } from '@onr/schema'
import { useParams, useSearch } from '@tanstack/react-router'
import dayjs from 'dayjs'

import {
  TeamConversationView,
  type TeamLinkConfig,
} from '@/components/templates/TeamConversationView/TeamConversationView.tsx'

import { useEveningMessagesPage } from './useEveningMessagesPage.ts'
import { useReadOnlyMessagingRail } from './useMessagingRail.ts'

const EveningMessagesPageContent: React.FC = () => {
  const { eveningId, teamId } = useParams({ from: '/regions/messages/$eveningId/$teamId' })
  const search = useSearch({ from: '/regions/messages/$eveningId/$teamId' })

  const railSearch = useMemo(
    () => ({
      regionName: search.regionName,
      piloteRegionId: search.piloteRegionId,
      eveningDate: search.eveningDate,
      regionId: search.regionId,
    }),
    [search.regionName, search.piloteRegionId, search.eveningDate, search.regionId]
  )

  const rail = useReadOnlyMessagingRail({
    destination: 'teams',
    teamsTo: '/regions/messages/$eveningId',
    teamsParams: { eveningId },
    channelId: LOUNGE_CHANNEL_ID,
    channelTo: '/regions/messages/$eveningId/channels/$channelId',
    channelParams: { eveningId, channelId: LOUNGE_CHANNEL_ID },
    search: railSearch,
  })

  const props = useEveningMessagesPage({
    eveningId: Number(eveningId),
    teamId,
    regionName: search.regionName ?? '',
    piloteRegionId: search.piloteRegionId ?? 0,
    rail,
  })

  const teamLinkConfig = useMemo(
    (): TeamLinkConfig => ({
      to: '/regions/messages/$eveningId/$teamId',
      params: (tid: string) => ({ eveningId, teamId: tid }),
      search: railSearch,
    }),
    [eveningId, railSearch]
  )

  const formattedDate = search.eveningDate
    ? dayjs(search.eveningDate).locale('fr-ca').format('dddd D MMMM YYYY')
    : undefined

  return <TeamConversationView {...props} teamLinkConfig={teamLinkConfig} readOnly readOnlyDate={formattedDate} />
}

export const EveningMessagesPage: React.FC = () => (
  <Suspense
    fallback={<div className="flex h-screen items-center justify-center bg-gray-800 text-white">Loading...</div>}>
    <EveningMessagesPageContent />
  </Suspense>
)
